import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, PackagePlus, Printer } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

import { InboundArrivalPackageCancelModal } from "./InboundArrivalPackageCancelModal";
import {
  cancelInboundArrivalPackage,
  INBOUND_ARRIVALS_QUERY_KEY,
  printInboundArrivalPackageLabels,
  type InboundDockShipment,
  type InboundDockShipmentUnit,
} from "./inboundApi";
import {
  formatDockShipmentLoggedSummary,
  formatInboundArrivalUnitHeading,
  parseInboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";

const MODAL_HELP =
  "Each row is one carton label printed at the dock. Reprint a lost tag, cancel a mistaken crate, "
  + "or add more cartons when another delivery arrives for the same purchase order.";

function formatLoggedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatExpectedArrival(isoDate: string | null): string {
  if (isoDate == null || isoDate.trim() === "") return "Expected date unknown";
  const date = new Date(`${isoDate.trim()}T12:00:00`);
  if (Number.isNaN(date.getTime())) return `Expected ${isoDate.trim()}`;
  return `Expected ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

export function InboundShipmentDetailModal({
  open,
  shipment,
  selectedPrinter,
  onClose,
  onAddArrival,
  printNotice,
  onClearPrintNotice,
}: {
  open: boolean;
  shipment: InboundDockShipment | null;
  selectedPrinter: string;
  onClose: () => void;
  onAddArrival: (shipment: InboundDockShipment) => void;
  printNotice?: string | null;
  onClearPrintNotice?: () => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<InboundDockShipmentUnit | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: INBOUND_ARRIVALS_QUERY_KEY });
  };

  const reprintMutation = useMutation({
    mutationFn: (input: { arrivalId: string; packageId?: string }) => {
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      return printInboundArrivalPackageLabels({
        arrivalId: input.arrivalId,
        printer,
        packageId: input.packageId,
      });
    },
    onSuccess: () => {
      setError(null);
      onClearPrintNotice?.();
    },
    onError: (mutationError) =>
      setError(mutationError instanceof Error ? mutationError.message : String(mutationError)),
  });

  const cancelMutation = useMutation({
    mutationFn: (packageId: string) => cancelInboundArrivalPackage(packageId),
    onSuccess: (result) => {
      setError(null);
      setPendingCancel(null);
      invalidate();
      if (result.arrivalCancelled) {
        onClose();
      }
    },
    onError: (mutationError) =>
      setError(mutationError instanceof Error ? mutationError.message : String(mutationError)),
  });

  if (!open || shipment == null) return null;

  const printerReady = selectedPrinter.trim() !== "";
  const busy = reprintMutation.isPending || cancelMutation.isPending;
  const arrivalIds = [...new Set(shipment.units.map((unit) => unit.arrival_id))];

  const handleReprintAll = (): void => {
    if (!printerReady || arrivalIds.length === 0) return;
    for (const arrivalId of arrivalIds) {
      reprintMutation.mutate({ arrivalId });
    }
  };

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inbound-shipment-detail-title"
      aria-busy={busy || undefined}
    >
      <InboundArrivalPackageCancelModal
        open={pendingCancel != null}
        packageNo={pendingCancel?.package_no ?? 0}
        scanLabel={pendingCancel?.scan_label ?? ""}
        confirming={cancelMutation.isPending}
        onCancel={() => setPendingCancel(null)}
        onConfirm={() => {
          if (pendingCancel == null) return;
          cancelMutation.mutate(pendingCancel.package_id);
        }}
      />
      <div className="my-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 id="inbound-shipment-detail-title" className="truncate text-lg font-semibold text-slate-900">
                  {shipment.po_number?.trim() ? `${shipment.po_number.trim()}, ` : null}
                  {shipment.vendor_name}
                </h2>
                <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {formatDockShipmentLoggedSummary(shipment.unit_count, shipment.units)}
                {shipment.has_expectation
                  ? `, ${formatExpectedArrival(shipment.expected_arrival)}`
                  : null}
              </p>
              {shipment.expected_tracking ? (
                <p className="mt-1 truncate text-xs text-slate-400">{shipment.expected_tracking}</p>
              ) : null}
            </div>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Close
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-5">
          {printNotice ? (
            <p role="status" className="flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {printNotice}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </p>
          ) : null}
          {!printerReady ? (
            <p className="text-sm text-amber-800">Pick a label printer to reprint tags.</p>
          ) : null}

          {shipment.units.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              No carton labels yet. Add an arrival to print tags for this shipment.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {shipment.units.map((unit) => (
                <li key={unit.package_id} className="flex items-center justify-between gap-3 px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-slate-900">
                      {formatInboundArrivalUnitHeading(
                        parseInboundArrivalUnitKind(unit.unit_kind),
                        unit.package_no,
                      )}
                    </p>
                    {unit.scan_label.trim() !== "" ? (
                      <p className="mt-1 font-mono text-sm leading-snug text-slate-700 whitespace-pre-wrap min-[800px]:text-base">
                        {unit.scan_label}
                      </p>
                    ) : null}
                    <p className="truncate text-xs text-slate-500">
                      {[
                        unit.carrier,
                        unit.tracking,
                        formatLoggedAt(unit.logged_at),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!printerReady || busy}
                      onClick={() =>
                        reprintMutation.mutate({
                          arrivalId: unit.arrival_id,
                          packageId: unit.package_id,
                        })
                      }
                    >
                      <Printer className="h-3.5 w-3.5" />
                      Reprint
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setPendingCancel(unit)}
                    >
                      Cancel
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            disabled={!printerReady || busy || shipment.units.length === 0}
            onClick={handleReprintAll}
          >
            <Printer className="h-4 w-4" />
            Reprint all
          </Button>
          <Button type="button" className="gap-2" disabled={busy} onClick={() => onAddArrival(shipment)}>
            <PackagePlus className="h-4 w-4" />
            Add arrival
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
